import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { fmtDateDMY, fmtMoney } from "../utils";
import type { QuickReportData } from "../types";

type Orientation = "portrait" | "landscape";
type Scaling = "smart" | "fit-width" | "fit-page";

interface Props {
  open: boolean;
  onClose: () => void;
  data: QuickReportData;
  businessName: string;
}

export default function PrintReportModal({ open, onClose, data, businessName }: Props) {
  const [orientation, setOrientation] = useState<Orientation>("portrait");
  const [scaling, setScaling] = useState<Scaling>("smart");
  const [showHeadersAllPages, setShowHeadersAllPages] = useState(true);

  if (!open) return null;

  const openPrintWindow = () => {
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) return;
    win.document.open();
    win.document.write(buildPrintHtml(data, businessName, orientation, scaling, showHeadersAllPages));
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
    }, 400);
  };

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col" style={{ maxHeight: "90vh" }}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#EEF1F4]">
          <div>
            <h2 className="text-lg font-bold text-ink">Print or save as PDF</h2>
            <p className="text-[12px] text-ink3 mt-0.5">To print, click Print below. To save as a PDF, click Save as PDF and pick that destination in the browser dialog.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-ink3 hover:text-ink">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid gap-5" style={{ gridTemplateColumns: "220px 1fr" }}>
            <div>
              <div className="text-[13px] font-bold text-ink mb-3">Report print settings</div>
              <div className="mb-3.5">
                <label className="block text-[10.5px] font-bold text-ink3 uppercase tracking-wider mb-1">Orientation</label>
                <select value={orientation} onChange={(e) => setOrientation(e.target.value as Orientation)} className="w-full h-9 border border-[#D8DEE6] rounded-md px-2 text-[12.5px] bg-white">
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>
              <div className="mb-3.5">
                <label className="block text-[10.5px] font-bold text-ink3 uppercase tracking-wider mb-1">Scaling</label>
                <select value={scaling} onChange={(e) => setScaling(e.target.value as Scaling)} className="w-full h-9 border border-[#D8DEE6] rounded-md px-2 text-[12.5px] bg-white">
                  <option value="smart">Smart page break</option>
                  <option value="fit-width">Fit to width</option>
                  <option value="fit-page">Fit to one page</option>
                </select>
              </div>
              <label className="inline-flex items-center gap-2 text-[12px] cursor-pointer">
                <input type="checkbox" checked={showHeadersAllPages} onChange={(e) => setShowHeadersAllPages(e.target.checked)} />
                Show report headers on all pages
              </label>
            </div>
            <div className="border border-[#D8DEE6] rounded-md overflow-hidden bg-[#FAFBFC]" style={{ minHeight: 400 }}>
              <iframe
                title="Report preview"
                srcDoc={buildPrintHtml(data, businessName, orientation, scaling, showHeadersAllPages)}
                className="w-full bg-white"
                style={{ border: 0, height: 500 }}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2.5 px-5 py-3 border-t border-[#EEF1F4]">
          <button type="button" onClick={openPrintWindow} className="h-9 px-4 border border-[#D8DEE6] rounded-[10px] text-[12.5px] font-semibold text-ink bg-white">Save as PDF</button>
          <button type="button" onClick={openPrintWindow} className="h-9 px-4 border border-ink rounded-[10px] text-[12.5px] font-semibold text-white bg-ink">Print</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => (({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" } as Record<string, string>)[c]));
}

function clearedLabel(c: string): string {
  if (c === "cleared") return "Cleared";
  if (c === "reconciled") return "Reconciled";
  return "Not cleared";
}

function buildPrintHtml(data: QuickReportData, businessName: string, orientation: Orientation, scaling: Scaling, showHeadersAllPages: boolean): string {
  const groupsHtml = data.groups.map((g) => `
    <tr style="background:#F0F2F5;"><td colspan="10" style="font-weight:700;padding:6px 8px;">${escapeHtml(g.label)} (${g.rows.length})</td></tr>
    ${g.rows.map((r) => `
      <tr>
        <td>${escapeHtml(r.distributionAccount)}</td>
        <td>${fmtDateDMY(r.date)}</td>
        <td>${escapeHtml(r.type)}</td>
        <td>${escapeHtml(r.refNo)}</td>
        <td>${escapeHtml(r.payee)}</td>
        <td>${escapeHtml(r.description)}</td>
        <td>${escapeHtml(r.accountName)}</td>
        <td>${clearedLabel(r.cleared)}</td>
        <td style="text-align:right;">${fmtMoney(r.amount)}</td>
        <td style="text-align:right;">${fmtMoney(r.balance)}</td>
      </tr>
    `).join("")}
    <tr style="background:#F7F9FB;font-weight:700;">
      <td colspan="8" style="padding:6px 8px;">Total for ${escapeHtml(g.label)}</td>
      <td style="text-align:right;">${fmtMoney(g.subtotal)}</td>
      <td style="text-align:right;">${fmtMoney(g.endBalance)}</td>
    </tr>
  `).join("");

  const zoomStyle = scaling === "fit-page" ? "body { zoom: 0.75; }" : "";
  const theadRepeat = showHeadersAllPages ? "thead { display: table-header-group; }" : "";

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8"/>
<title>Account quick report</title>
<style>
  @page { size: ${orientation}; margin: 0.5in; }
  body { font-family: -apple-system, Arial, sans-serif; margin: 0; padding: 20px; color: #0E1A1A; }
  .header { text-align: center; margin-bottom: 16px; }
  .header h1 { font-size: 15px; font-weight: 700; margin: 0; }
  .header h2 { font-size: 13px; font-weight: 700; margin: 2px 0 0; }
  .header .meta { font-size: 10px; color: #5A6B6B; margin-top: 3px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  thead th { background: #ECF0F4; padding: 6px 8px; text-align: left; border-bottom: 1px solid #D8DEE6; font-size: 9px; text-transform: uppercase; letter-spacing: 0.3px; font-weight: 700; }
  tbody td { padding: 5px 8px; border-bottom: 1px solid #EEF1F4; }
  tfoot td { font-weight: 800; padding: 8px; background: #FAFBFC; text-transform: uppercase; }
  ${theadRepeat}
  ${zoomStyle}
</style>
</head>
<body>
<div class="header">
  <h1>${escapeHtml(businessName)}</h1>
  <h2>Account quick report</h2>
  <div class="meta">${escapeHtml(data.accountName)} &middot; ${fmtDateDMY(data.from)} to ${fmtDateDMY(data.to)}</div>
</div>
<table>
  <thead>
    <tr>
      <th>Distribution account</th><th>Date</th><th>Type</th><th>Ref no</th><th>Payee</th><th>Description</th><th>Account</th><th>Cleared</th><th style="text-align:right;">Amount</th><th style="text-align:right;">Balance</th>
    </tr>
  </thead>
  <tbody>${groupsHtml}</tbody>
  <tfoot>
    <tr><td colspan="8">TOTAL</td><td style="text-align:right;">${fmtMoney(data.total)}</td><td style="text-align:right;">${fmtMoney(data.endBalance)}</td></tr>
  </tfoot>
</table>
</body>
</html>`;
}